/**
 * SOLID Review
 * S — Strong. One use case: catch the table up after a gap — the live round, then the room's suspended list.
 * O — Strong. A further read is one more leg here; the rules stay the Domain's.
 * L — Strong. Implements its use-case role exactly.
 * I — Strong. `execute`.
 * D — Strong. The decisions and server ports and the table's step, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type {
  ICommandNothing, ICommandActiveRoundRestored, ICommandSuspendedReconciled, IPayloadRoomRead,
} from '../../domain/ports/schemas/TableMoves';
import type { IPayloadServerRoomCode } from '../ports/contracts';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import { DONE } from '../shared/ClientSteps';
import type { IConfigDepTable } from './IConfigDepTable';

/**
 * CATCHING UP AFTER A GAP (FE1's resync; a use case since frontend-application-oo P2). Not one round trip: a plan, then two
 * INDEPENDENT reads — the active round, then the room's suspended list — each applied only if it answered, as the hook's two
 * try blocks were. No op of `ClientUseCases` reaches it: a connection that comes back after a gap starts it, unawaited.
 */
export class ResyncUseCase implements IUseCase<ICommandNothing, TPortOutcome> {
  constructor(private readonly deps: IConfigDepTable) {}

  async execute(ctx: IContextRequest, command: ICommandNothing): Promise<TPortOutcome> {
    const { decisions, rounds, rooms, step } = this.deps;
    const plan = await step.run(ctx, (input) => decisions.planResync(ctx, input, command));
    if (!plan.success) return plan;
    const read: IPayloadRoomRead = { roomId: plan.value.roomId };
    const round = await rounds.getActiveRoundForRestore(ctx, read);
    if (round.success) {
      const restored: ICommandActiveRoundRestored = { request: plan.value, activeRound: round.value };
      await step.run(ctx, (input) => decisions.activeRoundRestored(ctx, input, restored));
    }
    const code: IPayloadServerRoomCode = { roomCode: plan.value.roomCode };
    const lookup = await rooms.getRoomByCode(ctx, code);
    if (lookup.success) {
      const reconciled: ICommandSuspendedReconciled = { request: plan.value, suspendedPlayers: lookup.value?.suspendedPlayers ?? null };
      await step.run(ctx, (input) => decisions.suspendedReconciled(ctx, input, reconciled));
    }
    return DONE;
  }
}
